import type { ColDef, ExcelStyle } from 'ag-grid-community';
import type { ColumnType } from '../columns';
import type { Column } from '../columns/types';

/** Excel style class for text columns */
export const EXCEL_TEXT_COLUMN_CLASS = 'excel-text-column';

/**
 * Default Excel styles for text columns to ensure proper export formatting
 */
export const DEFAULT_AG_GRID_EXCEL_STYLES: ExcelStyle[] = [
  {
    id: EXCEL_TEXT_COLUMN_CLASS,
    dataType: 'String',
  },
];

/** Wrap Columns with EXCEL_TEXT_COLUMN_CLASS to ensure proper Excel export
 * - see https://www.ag-grid.com/javascript-data-grid/excel-export-data-types/
 * - This should be replaced once https://talostrading.atlassian.net/browse/UI-6908 is done
 */
export function wrapColumnWithExcelColumnClass(column: Column): ColDef['cellClass'] {
  const baseCellClass = column.cellClass;
  const isText = column.type !== undefined && TEXT_COLUMN_TYPES.has(column.type);
  if (isText) {
    if (typeof baseCellClass === 'function') {
      return params => {
        const baseResult = baseCellClass(params);
        return appendExcelTextColumnClass(baseResult);
      };
    }
    return appendExcelTextColumnClass(baseCellClass);
  }
  return baseCellClass;
}

/**
 * Appends EXCEL_TEXT_COLUMN_CLASS to a cellClass value (string, string[], null, or undefined)
 */
function appendExcelTextColumnClass(cellClass: string | string[] | null | undefined): string | string[] {
  return typeof cellClass === 'string'
    ? `${cellClass} ${EXCEL_TEXT_COLUMN_CLASS}`
    : Array.isArray(cellClass)
      ? [...cellClass, EXCEL_TEXT_COLUMN_CLASS]
      : EXCEL_TEXT_COLUMN_CLASS;
}

/**
 * Set of column types that should be exported as String type in Excel
 */
const TEXT_COLUMN_TYPES = new Set<ColumnType>([
  'text',
  'textLiveEdit',
  'email',
  'id',
  'prettyText',
  'securityText',
  'stringArray',
  'textArray',
]);
